/**
 * Mask value for sensitive data in logs and Sentry.
 */
const REDACTED = '[REDACTED]';

/**
 * Keys that indicate sensitive data in this project.
 * Aligned with order/customer/recipient/billing/payment structures.
 * Values for these keys will be replaced with REDACTED.
 */
const SENSITIVE_KEYS = [
  'accessToken',
  'authorization',
  'token',
  'nonce',
  'paymentMethodNonce',
  'methodReferenceId',
  'referenceId',
  'card_last_4_digits',
  'card_token',
  'card_type',
  "customer_email",
  "greetings_on_card",
  "customer_phone",
  "phone",
  "phone_number",
  "customer_alt_phone",
  "alt_phone",
  "customer_alt_phone_country_code",
  "alt_country_code",
];

/**
 * Checks if a key is sensitive (exact or partial match, case-insensitive).
 * @param {string} key - Object key to check
 * @returns {boolean}
 */
function isSensitiveKey(key) {
  if (!key || typeof key !== 'string') return false;
  const lower = key.toLowerCase();
  return SENSITIVE_KEYS.some(
    (sk) => lower === sk.toLowerCase() || lower.includes(sk.toLowerCase())
  );
}

/**
 * Sanitizes an object by replacing sensitive field values with [REDACTED].
 * Works recursively for nested objects and arrays of objects.
 *
 * @param {*} data - Value to sanitize (object, array, or primitive)
 * @returns {*} Sanitized copy; primitives returned as-is
 */
export function sanitizeData(data) {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => sanitizeData(item));
  }

  const sanitized = {};
  for (const key of Object.keys(data)) {
    if (isSensitiveKey(key)) {
      sanitized[key] = REDACTED;
    } else {
      sanitized[key] = sanitizeData(data[key]);
    }
  }
  return sanitized;
}

/**
 * Builds a Sentry-ready extra object with only sanitized (masked) data.
 * Use this for all Sentry.captureMessage / captureException extra payloads.
 *
 * @param {Object} options
 * @param {*} [options.error] - Error or error response data
 * @param {*} [options.body] - Request body
 * @param {string} [options.url] - Request URL
 * @param {string} [options.message] - Error message
 * @param {string} [options.generateTime] - Timestamp
 * @param {string} [options.functionName] - Function name
 * @returns {Object} Sanitized object safe to send to Sentry
 */
export function buildSentryExtra({
  error,
  body,
  url,
  message,
  generateTime,
  functionName,
}) {
  const extra = {};
  if (url !== undefined) extra.url = url;
  if (message !== undefined) extra.message = message;
  if (generateTime !== undefined) extra.generateTime = generateTime;
  if (functionName !== undefined) extra.functionName = functionName;
  if (error !== undefined) {
    try {
      extra.error =
        typeof error === 'object'
          ? JSON.stringify(sanitizeData(error))
          : String(error);
    } catch {
      extra.error = REDACTED;
    }
  }
  if (body !== undefined) {
    try {
      extra.body =
        typeof body === 'object'
          ? JSON.stringify(sanitizeData(body))
          : REDACTED;
    } catch {
      extra.body = REDACTED;
    }
  }
  return extra;
}
